import React, { useCallback, useEffect, useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import { profitLockGet, profitLockSet } from "../lib/api";

const BADGE = {
  OFF: "bg-slate-500/15 text-slate-300",
  ARMED: "bg-cyan-500/15 text-cyan-200",
  PROTECT: "bg-amber-400/15 text-amber-300",
  HALTED: "bg-rose-500/15 text-rose-300",
};
const usd = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export const ProfitLockControl = () => {
  const [pl, setPl] = useState(null);
  const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => profitLockGet().then((d) => { setPl(d); setAmt((a) => (a === "" ? String(d.profit_lock_usd) : a)); }).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const save = async (body) => {
    setBusy(true);
    try { const d = await profitLockSet(body); setPl(d); setAmt(String(d.profit_lock_usd)); toast.success("Profit Lock saved — applies on the next floor round"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Profit Lock update failed"); }
    finally { setBusy(false); }
  };
  if (!pl) return null;
  const pending = Number(amt) !== Number(pl.profit_lock_usd);
  return (
    <div data-testid="profit-lock-control" className="rounded-xl border border-amber-300/20 bg-amber-300/[0.03] p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Lock className="w-3.5 h-3.5 text-amber-300" />
        <span className="text-xs font-bold text-amber-200">Profit Lock</span>
        <Switch data-testid="profit-lock-toggle" checked={pl.enabled} disabled={busy} onCheckedChange={(v) => save({ enabled: v })}
          className="data-[state=checked]:bg-amber-400 data-[state=unchecked]:bg-slate-600 border border-amber-300/40" />
        <span data-testid="profit-lock-toggle-label" className="text-[10px] font-bold text-amber-200/80">{pl.enabled ? "ON" : "OFF"}</span>
        <span data-testid="profit-lock-state" className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${BADGE[pl.state] || BADGE.OFF}`}>{pl.state}</span>
        <div className="ml-auto flex items-center gap-1">
          <span className="text-xs text-amber-200/70">$</span>
          <input data-testid="profit-lock-amount-input" type="number" min="1" step="50" value={amt} onChange={(e) => setAmt(e.target.value)}
            className="w-24 bg-[#030712] border border-amber-300/30 rounded-md px-2 py-1 text-xs text-amber-100 font-mono focus:outline-none focus:border-amber-300" />
          <button data-testid="profit-lock-save-btn" disabled={busy || !pending || !(Number(amt) >= 1)} onClick={() => save({ profit_lock_usd: Number(amt) })}
            className="px-2.5 py-1 rounded-md text-[11px] font-bold border border-amber-300/40 text-amber-200 disabled:opacity-40 transition-colors hover:bg-amber-300/10">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
          </button>
        </div>
      </div>
      <p data-testid="profit-lock-net-line" className="text-xs font-mono text-amber-100/80">
        day net <span className={pl.net >= 0 ? "text-emerald-300" : "text-rose-300"}>{usd(pl.net)}</span> vs lock {usd(pl.active_usd)}
        {pl.peak > 0 && <> · peak {usd(pl.peak)} · halt below {usd(pl.peak * 0.75)}</>}
        {pl.active_usd !== pl.profit_lock_usd && <span className="text-amber-300"> · {usd(pl.profit_lock_usd)} from next round</span>}
      </p>
      {pl.state === "HALTED" && pl.halt_reason && <p data-testid="profit-lock-halt-reason" className="text-[11px] text-rose-300/90">{pl.halt_reason}</p>}
      {pl.state === "PROTECT" && <p data-testid="profit-lock-protect-note" className="text-[11px] text-amber-300/90">PROTECT since {new Date(pl.protect_at).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" })} ET — stops at breakeven+fees, momentum lane only, half size, no re-entries.</p>}
    </div>
  );
};
